import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  EntitySearchFilterOperator,
  EntitySearchNumberBetweenFilterOperator,
  EntitySearchNumberFilterOperator,
  ItemTemplateSearchFilterPropertyFilterInput,
  ProductSearchFilterExpressionFilterKey,
  ProductSearchFilterExpressionFilterKind,
} from "../../../../clients/graphqlTypes.js";
import { ItemProductSearchFilterDefinitionsFragment } from "../../products/productSearchFilters.generated.js";
import {
  BoolFilterOperatorOptions,
  NumberFilterOperatorOptions,
  PriceFilterOperatorOptions,
  TextFilterOperatorOptions,
} from "../../products/ProductSearchFilterOperatorOptions.js";
import { useProductSearchFilterMutations } from "../useProductSearchFilterDefinitionMutations.js";
import { useItemProductSearchFilterExpressionModalQuery } from "./ItemProductSearchFilterExpressionModal.generated.js";
import { SearchFilterExpressionModal } from "./SearchFilterExpressionModal.js";

interface Props extends Omit<
  React.ComponentProps<typeof SearchFilterExpressionModal>,
  "handleComplete" | "allowedSearchFilterOperators" | "filterLabel"
> {
  filterKind: ProductSearchFilterExpressionFilterKind;
  filterLabel: ProductSearchFilterExpressionFilterKey;
  propertyKey?: string;
  refetchQueries?: string[];
}

/**
 * Edit one product search filter's expression from the diagnostics pages,
 * addressed by filter key rather than by a row in the product box.
 */
export function ItemProductSearchFilterExpressionModal({
  projectId,
  docId,
  itemId,
  filterKind,
  filterLabel: filterKey,
  propertyKey,
  expression,
  operator,
  handleClose,
  refetchQueries,
}: Props) {
  const { t } = useTranslate(["Product"]);

  const client = useApolloClient();
  const query = useItemProductSearchFilterExpressionModalQuery({
    client,
    variables: { docId, itemId },
  });
  const definitions = getDataOrNull(
    query.data?.item
  )?.productSearchFilterDefinitions;

  const { writeFilters, setPropertyFilterSlot } =
    useProductSearchFilterMutations({
      projectId,
      docId,
      itemId,
      definitions: definitions ?? EMPTY_DEFINITIONS,
      refetchQueries,
      refetchWizardQuestions: noRefetch,
    });

  if (!definitions) return null;

  const modalProps = {
    projectId,
    docId,
    itemId,
    expression,
    handleClose,
  };

  if (filterKey === "listPrice") {
    return (
      <SearchFilterExpressionModal
        {...modalProps}
        filterLabel={t("List price", { ns: "Product" })}
        operator={priceOperator(operator)}
        allowedSearchFilterOperators={PriceFilterOperatorOptions}
        handleComplete={async (value, close) => {
          await writeFilters({
            listPriceFilter:
              value.operator === "between"
                ? {
                    numberBetweenFilterComputed: {
                      expr: value.expr,
                      operatorNumberBetween: value.operator,
                    },
                  }
                : {
                    numberFilterComputed: {
                      expr: value.expr,
                      operatorNumber: numberOperator(value.operator),
                    },
                  },
          });
          close();
        }}
      />
    );
  }

  if (filterKey === "brand" || filterKey === "brandLine") {
    const field = filterKey === "brand" ? "brandFilter" : "brandLineFilter";
    return (
      <SearchFilterExpressionModal
        {...modalProps}
        filterLabel={
          filterKey === "brand"
            ? t("Brand", { ns: "Product" })
            : t("Brand line", { ns: "Product" })
        }
        operator={operator === "in" ? "in" : "eq"}
        allowedSearchFilterOperators={TextFilterOperatorOptions}
        handleComplete={async (value, close) => {
          await writeFilters({
            [field]:
              value.operator === "in"
                ? {
                    textInFilterComputed: {
                      expr: value.expr,
                      operatorTextIn: value.operator,
                    },
                  }
                : {
                    textFilterComputed: {
                      expr: value.expr,
                      operatorText: "eq",
                    },
                  },
          });
          close();
        }}
      />
    );
  }

  if (filterKey === "properties" && propertyKey) {
    const options =
      filterKind === "number"
        ? NumberFilterOperatorOptions
        : filterKind === "bool"
          ? BoolFilterOperatorOptions
          : TextFilterOperatorOptions;

    return (
      <SearchFilterExpressionModal
        {...modalProps}
        filterLabel={propertyKey}
        operator={operator}
        allowedSearchFilterOperators={options}
        handleComplete={async (value, close) => {
          await setPropertyFilterSlot(
            propertyKey,
            propertyClauseComputed(filterKind, propertyKey, value)
          );
          close();
        }}
      />
    );
  }

  return null;
}

const EMPTY_DEFINITIONS = {
  __typename: "ItemProductSearchFilterDefinitions",
  itemId: "",
  propertyFilters: [],
  listPriceFilter: null,
  netPriceFilter: null,
  supplierIdFilter: null,
  productTypeIdFilter: null,
  brandFilter: null,
  brandLineFilter: null,
} as const satisfies ItemProductSearchFilterDefinitionsFragment;

const noRefetch = async () => {};

function priceOperator(
  operator: EntitySearchFilterOperator
): EntitySearchNumberFilterOperator | EntitySearchNumberBetweenFilterOperator {
  return operator === "between" ||
    operator === "eq" ||
    operator === "gt" ||
    operator === "gte" ||
    operator === "lt" ||
    operator === "lte"
    ? operator
    : "eq";
}

function numberOperator(
  operator: EntitySearchFilterOperator
): EntitySearchNumberFilterOperator {
  return operator === "eq" ||
    operator === "gt" ||
    operator === "gte" ||
    operator === "lt" ||
    operator === "lte"
    ? operator
    : "eq";
}

/** The computed clause variant for one product property filter. */
function propertyClauseComputed(
  filterKind: ProductSearchFilterExpressionFilterKind,
  key: string,
  value: { expr: string; operator: EntitySearchFilterOperator }
): ItemTemplateSearchFilterPropertyFilterInput {
  const { expr, operator } = value;
  if (filterKind === "bool") {
    return operator === "in"
      ? { boolInFilterComputed: { key, operatorBoolIn: operator, expr } }
      : { boolFilterComputed: { key, operatorBool: "eq", expr } };
  }
  if (filterKind === "number") {
    if (operator === "in") {
      return {
        numberInFilterComputed: { key, operatorNumberIn: operator, expr },
      };
    }
    if (operator === "between") {
      return {
        numberBetweenFilterComputed: {
          key,
          operatorNumberBetween: operator,
          expr,
        },
      };
    }
    return {
      numberFilterComputed: {
        key,
        operatorNumber: numberOperator(operator),
        expr,
      },
    };
  }
  return operator === "in"
    ? { textInFilterComputed: { key, operatorTextIn: operator, expr } }
    : { textFilterComputed: { key, operatorText: "eq", expr } };
}
